import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { apiClient } from '../api/client';
import { Message, Chat } from '../types';
import { useTelegram } from '../hooks/useTelegram';
import { formatMessageContent } from '../utils/messageFormatters';
import './MessageList.css';

export function MessageList() {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useTelegram();
  const [messages, setMessages] = useState<Message[]>([]);
  const [_, setChat] = useState<Chat | null>(null);
  const [chatName, setChatName] = useState<string>('');
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [totalMessages, setTotalMessages] = useState(0);
  const showDeletedOnly = true;
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chatId) {
      loadMessages();

      const state = location.state as { chatName?: string; avatarUrl?: string };
      if (state?.chatName) {
        setChatName(state.chatName);
        setAvatarUrl(state.avatarUrl);
      } else {
        loadChat();
      }
    }
  }, [chatId, location, showDeletedOnly]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout: ReturnType<typeof setTimeout> | null = null;

    const handleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }

      scrollTimeout = setTimeout(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;

        const distanceToTop = Math.abs(scrollTop) + clientHeight;
        const threshold = scrollHeight - 300;

        console.log('Scroll check:', { distanceToTop, threshold, hasMore, loadingMore });

        if (distanceToTop >= threshold && hasMore && !loadingMore) {
          loadMoreMessages();
        }
      }, 150);
    };

    container.addEventListener('scroll', handleScroll);
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [hasMore, loadingMore, messages.length]);

  async function loadMessages() {
    if (!chatId) return;

    try {
      setLoading(true);
      console.log('Loading messages for chatId:', chatId);
      const response = await apiClient.getMessages(chatId, 0, 100, showDeletedOnly);
      console.log('Messages loaded:', response);
      setMessages(response.messages);
      setTotalMessages(response.total);
      setHasMore(response.messages.length < response.total);
    } catch (err) {
      console.error('Error loading messages:', err);
      setError(err instanceof Error ? err.message : 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }

  async function loadMoreMessages() {
    if (!chatId || loadingMore || !hasMore) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    // Сохраняем текущую позицию перед загрузкой
    const previousScrollHeight = container.scrollHeight;
    const previousScrollTop = container.scrollTop;

    try {
      setLoadingMore(true);
      const skip = messages.length;
      console.log('Loading more messages, skip:', skip);
      const response = await apiClient.getMessages(chatId, skip, 100, showDeletedOnly);
      console.log('More messages loaded:', response.messages.length);

      if (response.messages.length > 0) {
        setMessages((prev) => {
          const existingIds = new Set(prev.map(m => m.messageId));

          const newMessages = response.messages.filter(
            msg => !existingIds.has(msg.messageId)
          );

          console.log('Filtered new messages:', newMessages.length, 'from', response.messages.length);

          const combined = [...prev, ...newMessages];
          setHasMore(combined.length < totalMessages);
          return combined;
        });

        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            const scrollDiff = newScrollHeight - previousScrollHeight;
            container.scrollTop = previousScrollTop - scrollDiff;
          }
        }, 0);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Error loading more messages:', err);
    } finally {
      setLoadingMore(false);
    }
  }

  async function loadChat() {
    if (!chatId) return;

    try {
      const chatData = await apiClient.getChat(chatId);
      setChat(chatData);
      setChatName(chatData.chatName);
      setAvatarUrl(chatData.avatarUrl);
    } catch (err) {
      console.error('Failed to load chat info:', err);
    }
  }

  function formatDate(date: Date): string {
    const d = new Date(date);
    return d.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function isOwnMessage(message: Message): boolean {
    if (!user) return false;
    return message.from.username === user.username ||
           (message.from.firstName === user.first_name &&
            message.from.lastName === user.last_name);
  }

  function scrollToMessage(messageId: string) {
    const element = messageRefs.current.get(messageId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.style.animation = 'none';
      setTimeout(() => {
        element.style.animation = 'highlight 1s ease';
      }, 10);
    }
  }

  console.log('MessageList render:', { loading, error, messagesCount: messages.length, chatId, chatName });

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  if (error) {
    return <div className="error">Ошибка: {error}</div>;
  }

  return (
    <div className="message-list">
      <div className="message-list-header">
        <button className="back-button" onClick={() => navigate('/chats')}>
          ←
        </button>
        {chatName && (
          <>
            <div className="header-avatar">
              {avatarUrl ? (
                <img src={avatarUrl} alt={chatName} />
              ) : (
                chatName.charAt(0).toUpperCase()
              )}
            </div>
            <h2>{chatName}</h2>
          </>
        )}
      </div>
      {messages.length === 0 ? (
        <div className="empty-state">
          {showDeletedOnly ? 'Нет удалённых сообщений' : 'Нет сообщений'}
        </div>
      ) : (
        <div className="messages" ref={messagesContainerRef}>
          {loadingMore && (
            <div className="loading-more">Загрузка...</div>
          )}
          {messages.map((message) => {
            const isOwn = isOwnMessage(message);
            return (
              <div
                key={message.messageId}
                ref={(el) => {
                  if (el) {
                    messageRefs.current.set(message.messageId, el);
                  } else {
                    messageRefs.current.delete(message.messageId);
                  }
                }}
                className={`message-wrapper ${isOwn ? 'own' : 'other'}`}
              >
                <div className="message-bubble">
                    {message.replyTo && (
                      <div
                        className="message-reply"
                        onClick={() => scrollToMessage(message.replyTo!.messageId)}
                      >
                        {message.replyTo.from && (
                          <div className="reply-author">
                            {message.replyTo.from.firstName || 'Unknown'}
                            {message.replyTo.from.lastName && ` ${message.replyTo.from.lastName}`}
                          </div>
                        )}
                        <div className="reply-text">
                          {formatMessageContent(
                            message.replyTo.text || '',
                            message.replyTo.messageType || 'text'
                          ) || 'Сообщение'}
                        </div>
                      </div>
                    )}
                    {!isOwn && (
                      <div className="message-author">
                        {message.from.firstName}
                        {message.from.lastName && ` ${message.from.lastName}`}
                      </div>
                    )}
                    <div className="message-text">
                      {formatMessageContent(message.text, message.messageType)}
                    </div>
                    <div className="message-time">{formatDate(message.date)}</div>
                  </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}